// Client-side checks that mirror the domain validation (backend/internal/domain,
// Validate with field paths). They exist for fast feedback only: control is the
// authority and its 422 lands on the same paths. Limits are the domain's.
// Messages follow the console language (shared/i18n/messages.ts sharedUi).

import { common, sharedUi } from '@/shared/i18n/messages'

const v = sharedUi.t

export const LIMITS = {
  /** domain.MaxNameLen */
  nameLen: 120,
  /** domain.MinPassphraseLen..MaxPassphraseLen (SRT) */
  passphraseMin: 10,
  passphraseMax: 79,
  /** domain.MinLatencyMs..MaxLatencyMs */
  latencyMin: 20,
  latencyMax: 8000,
  /** domain.MaxStreamIDLen */
  streamIdLen: 512,
  /** RTMP stream key length */
  streamKeyMin: 8,
  streamKeyMax: 128,
  /** domain.MaxInputsPerChannel */
  inputsPerChannel: 4,
} as const

/** Parses a whole decimal integer; null for anything else (empty, "1.5", "12a"). */
export function parseIntStrict(text: string): number | null {
  const t = text.trim()
  if (!/^-?\d+$/.test(t)) return null
  const n = Number(t)
  return Number.isSafeInteger(n) ? n : null
}

export function nameError(s: string, required = true): string | null {
  if (s.trim() === '') return required ? v('vRequired') : null
  if (s.length > LIMITS.nameLen) return v('vMaxLen', { max: LIMITS.nameLen })
  if (s !== s.trim()) return v('vNoEdgeSpaces')
  return null
}

/** Printable ASCII without spaces (SRT streamid alphabet). */
export function isPrintableAscii(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    if (c <= 0x20 || c >= 0x7f) return false
  }
  return true
}

const CREDENTIAL_MARKERS = ['password=', 'passphrase=', 'passwd=', 'token=', 'secret=', 'key=']

/** An INBOUND streamid is a lookup key shown in the console: it must not carry credentials. */
export function inboundStreamIdError(s: string, required: boolean): string | null {
  if (s === '') return required ? v('vRequired') : null
  if (s.length > LIMITS.streamIdLen) return v('vMaxLen', { max: LIMITS.streamIdLen })
  if (!isPrintableAscii(s)) return v('vPrintableAscii')
  const lower = s.toLowerCase()
  const bad = CREDENTIAL_MARKERS.find((m) => lower.includes(m))
  if (bad) return v('vNoCredential', { marker: bad.slice(0, -1) })
  return null
}

/** An OUTBOUND streamid (secret) - same alphabet, no credential check (it is where they belong). */
export function outboundStreamIdError(s: string): string | null {
  if (s.length > LIMITS.streamIdLen) return v('vMaxLen', { max: LIMITS.streamIdLen })
  if (!isPrintableAscii(s)) return v('vPrintableAscii')
  return null
}

export function passphraseError(value: string | null): string | null {
  if (value === null) return null
  if (value.length < LIMITS.passphraseMin || value.length > LIMITS.passphraseMax) {
    return v('vLengthRange', { min: LIMITS.passphraseMin, max: LIMITS.passphraseMax })
  }
  return null
}

export function rangeError(text: string, min: number, max: number, unit = ''): string | null {
  const n = parseIntStrict(text)
  if (n === null) return v('vInteger')
  if (n < min || n > max) return unit ? v('vRangeUnit', { min, max, unit }) : v('vRange', { min, max })
  return null
}

export function latencyError(text: string): string | null {
  return rangeError(text, LIMITS.latencyMin, LIMITS.latencyMax, common.t('unitMs'))
}

/** A DNS name or an IP literal, no scheme or port. */
export function hostError(h: string): string | null {
  if (h === '') return v('vRequired')
  if (h.length > 253) return v('vHostTooLong')
  if (isIPv4(h) || isIPv6(h)) return null
  for (const label of h.split('.')) {
    if (label === '' || label.length > 63 || !/^[A-Za-z0-9_]([A-Za-z0-9_-]*[A-Za-z0-9_])?$/.test(label)) {
      return v('vHost')
    }
  }
  return null
}

function isIPv4(s: string): boolean {
  const parts = s.split('.')
  return parts.length === 4 && parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255 && (p === '0' || !p.startsWith('0')))
}

function isIPv6(s: string): boolean {
  if (!s.includes(':') || !/^[0-9A-Fa-f:.]+$/.test(s)) return false
  const doubles = s.split('::').length - 1
  if (doubles > 1) return false
  const groups = s.split(':').filter((g) => g !== '')
  const last = groups.at(-1) ?? ''
  const v4tail = last.includes('.')
  if (v4tail && !isIPv4(last)) return false
  const hexGroups = v4tail ? groups.slice(0, -1) : groups
  if (!hexGroups.every((g) => /^[0-9A-Fa-f]{1,4}$/.test(g))) return false
  const width = hexGroups.length + (v4tail ? 2 : 0)
  return doubles === 1 ? width < 8 : width === 8
}

/** "10.0.0.0/8", "2001:db8::/32" (netip.ParsePrefix: the prefix length is mandatory). */
export function isCidr(s: string): boolean {
  const m = /^([^/]+)\/(\d{1,3})$/.exec(s)
  if (!m) return false
  const [, addr = '', bitsText = ''] = m
  const bits = Number(bitsText)
  if (isIPv4(addr)) return bits <= 32
  if (isIPv6(addr)) return bits <= 128
  return false
}
